/**
 * Norce Checkout Composable
 * Main state management for Norce Checkout with multiple payment adapters:
 * - Adyen: Card payments, Apple Pay, Google Pay via Adyen Drop-in
 * - Klarna: Klarna Checkout widget (embedded HTML snippet)
 * - Walley: Walley Checkout widget (embedded HTML snippet)
 * - NonPsp: Invoice payments, free orders (no external PSP)
 */

import { ref, readonly, computed } from 'vue';
import useNorceCheckoutApi from './useNorceCheckoutApi';
import { EDITABLE_ORDER_STATES } from './types';
import type {
  NorceCheckoutState,
  AdyenTransactionData,
  AdyenResultCode,
  AdyenAction,
  NorceOrderResult,
  OrderState,
  PaymentAdapter,
  PaymentData,
  NorceCheckoutCustomer,
  UpdateAddressRequest
} from './types';

// Type for checkPaymentStatus result
export type PaymentStatusResult = {
  success: boolean;
  adapterSwitched: boolean;
  previousAdapter: PaymentAdapter | null;
  currentAdapter: PaymentAdapter | null;
};

// Module-level state (shared across components)
const basketUuid = ref<string | null>(null);
const orderId = ref<string | null>(null);
const orderState = ref<OrderState | null>(null);
const paymentId = ref<string | null>(null);
const paymentAdapter = ref<PaymentAdapter | null>(null);
const payment = ref<PaymentData | null>(null); // Unified payment data
const customer = ref<NorceCheckoutCustomer | null>(null);
const availableAdapters = ref<PaymentAdapter[]>([]);
const defaultAdapter = ref<PaymentAdapter | null>(null);
const hasInvoice = ref(false);
const isInitializing = ref(false);
const isProcessing = ref(false);
const isCheckingStatus = ref(false);
const isUpdatingAddress = ref(false);
const isSwitchingAdapter = ref(false);
const error = ref<string | null>(null);
const resultCode = ref<AdyenResultCode | null>(null);

export default function useNorceCheckout() {
  const api = useNorceCheckoutApi();

  // Computed helpers for checking payment adapter type
  const isAdyen = computed(() => paymentAdapter.value === 'adyen');
  const isKlarna = computed(() => paymentAdapter.value === 'klarna');
  const isWalley = computed(() => paymentAdapter.value === 'walley');
  const isNonPsp = computed(() => paymentAdapter.value === 'nonpsp');

  // Whether the order is in a state that allows /customer address updates.
  // Defaults to true when orderState is unknown (backend may omit it) to preserve
  // existing behaviour for markets/versions that don't return the field yet.
  const isOrderEditable = computed(() => !orderState.value || EDITABLE_ORDER_STATES.includes(orderState.value));

  // Check if multiple PSP adapters are available (for showing adapter selector)
  const hasMultiplePspAdapters = computed(() => {
    const pspAdapters = availableAdapters.value.filter(a => a !== 'nonpsp');
    return pspAdapters.length > 1;
  });

  /**
   * Initialize checkout session
   * Creates order in Norce and gets payment configuration based on adapter type
   */
  const initialize = async(uuid: string): Promise<boolean> => {
    if (isInitializing.value) {
      return false;
    }

    isInitializing.value = true;
    error.value = null;
    resultCode.value = null;

    // Clear any orderId/orderState carried over from a previous session so a stale
    // (e.g. completed) order can never be reused. Only the fresh response sets them.
    orderId.value = null;
    orderState.value = null;

    try {
      const response = await api.initializeCheckout(uuid);

      if (response) {
        basketUuid.value = uuid;
        orderId.value = response.orderId;
        orderState.value = response.orderState || null;
        paymentAdapter.value = response.paymentAdapter || 'adyen'; // Default to adyen for backwards compatibility
        payment.value = response.payment || null;
        paymentId.value = response.payment?.paymentId || response.paymentId || null;
        customer.value = response.customer || null;

        return true;
      }

      error.value = api.lastError?.value?.message || 'Failed to initialize checkout';
      return false;
    } catch (e) {
      error.value = 'An unexpected error occurred during initialization';
      console.error('Norce Checkout initialization error:', e);
      return false;
    } finally {
      isInitializing.value = false;
    }
  };

  /**
   * Submit payment to Adyen via Norce
   * Handles the main payment transaction (Adyen only)
   */
  const submitPayment = async(
    transactionData: AdyenTransactionData
  ): Promise<{ success: boolean; resultCode?: AdyenResultCode; action?: AdyenAction; order?: NorceOrderResult; refusalReason?: string; is409?: boolean; canRetry?: boolean }> => {
    if (!orderId.value || !paymentId.value) {
      error.value = 'Checkout not initialized';
      return { success: false };
    }

    if (isProcessing.value) {
      return { success: false };
    }

    isProcessing.value = true;
    error.value = null;

    try {
      const response = await api.processAdyenTransaction(
        orderId.value,
        paymentId.value,
        transactionData
      );
      // Capture immediately — lastErrorCode is module-level in useApiFetch and any
      // concurrent request (status check, cart refresh) can overwrite it after this await.
      const transactionErrorCode = api.lastErrorCode?.value;

      if (response) {
        resultCode.value = response.resultCode;

        // Handle different result codes
        if (response.resultCode === 'Authorised') {
          return { success: true, resultCode: response.resultCode, order: response.order };
        }

        // Action required (3DS, redirect, etc.)
        if (response.action) {
          return { success: false, resultCode: response.resultCode, action: response.action };
        }

        // Genuine declines — user can retry with a different card
        if (response.resultCode === 'Refused') {
          const reason = response.refusalReason || 'Payment was refused';
          error.value = `${reason}. Please try another payment method.`;
          return { success: false, resultCode: response.resultCode, canRetry: true, refusalReason: response.refusalReason };
        }

        if (response.resultCode === 'Cancelled') {
          error.value = 'Payment was cancelled.';
          return { success: false, canRetry: true };
        }

        if (response.resultCode === 'Error') {
          error.value = 'A payment error occurred. Please try again.';
        }

        return { success: false, resultCode: response.resultCode };
      }

      // 409 means a payment is already in progress (concurrent request blocked)
      if (transactionErrorCode === 409) {
        return { success: false, is409: true };
      }

      error.value = api.lastError?.value?.message || 'Failed to process payment';
      return { success: false };
    } catch (e) {
      error.value = 'An unexpected error occurred during payment';
      console.error('Norce Checkout payment error:', e);
      return { success: false };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Submit additional details (for 3DS authentication, Swish, etc.)
   * Called after user completes 3DS challenge or redirect payment (Adyen only)
   */
  const submitDetails = async(
    stateData: Record<string, unknown>
  ): Promise<{ success: boolean; resultCode?: AdyenResultCode; action?: AdyenAction; order?: NorceOrderResult }> => {
    if (!orderId.value || !paymentId.value) {
      error.value = 'Checkout not initialized';
      return { success: false };
    }

    if (isProcessing.value) {
      return { success: false };
    }

    isProcessing.value = true;
    error.value = null;

    try {
      // Extract details and paymentData from Adyen state
      // For 3DS: stateData = { redirectResult: "..." } or { details: { ... } }
      // For Swish/redirect: stateData = { details: { payload: "..." }, paymentData: "..." }
      const { paymentData, ...rest } = stateData;
      const details = rest.details ? rest.details as Record<string, unknown> : rest;

      const response = await api.completeAdyenDetails(
        orderId.value,
        paymentId.value,
        details,
        paymentData as string | undefined
      );

      if (response) {
        resultCode.value = response.resultCode;

        if (response.resultCode === 'Authorised') {
          return { success: true, resultCode: response.resultCode, order: response.order };
        }

        if (response.action) {
          return { success: false, resultCode: response.resultCode, action: response.action };
        }

        if (response.resultCode === 'Refused') {
          error.value = 'Payment was refused after authentication.';
        }

        return { success: false, resultCode: response.resultCode };
      }

      error.value = api.lastError?.value?.message || 'Failed to complete authentication';
      return { success: false };
    } catch (e) {
      error.value = 'An unexpected error occurred during authentication';
      console.error('Norce Checkout details error:', e);
      return { success: false };
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Refresh payment session after cart changes
   * Updates Adyen payment methods configuration
   */
  const refresh = async(): Promise<boolean> => {
    if (!orderId.value || !paymentId.value) {
      return false;
    }

    // Only refresh for Adyen payments
    if (paymentAdapter.value !== 'adyen') {
      return true;
    }

    try {
      const response = await api.refreshAdyenPayment(orderId.value, paymentId.value);

      if (response) {
        payment.value = response.payment;
        return true;
      }

      return false;
    } catch (e) {
      console.error('Norce Checkout refresh error:', e);
      return false;
    }
  };

  /**
   * Check payment status after cart/customer changes
   * Detects adapter switches (e.g., adyen <-> nonpsp) and updates state accordingly
   *
   * Call this with debouncing (e.g., 1.5s delay) after basket or customer modifications
   * to allow backend time to sync changes.
   *
   * @returns PaymentStatusResult with info about adapter switches
   */
  const checkPaymentStatus = async(): Promise<PaymentStatusResult> => {
    if (!basketUuid.value) {
      return {
        success: false,
        adapterSwitched: false,
        previousAdapter: null,
        currentAdapter: null,
      };
    }

    // Prevent concurrent status checks
    if (isCheckingStatus.value) {
      return {
        success: false,
        adapterSwitched: false,
        previousAdapter: paymentAdapter.value,
        currentAdapter: paymentAdapter.value,
      };
    }

    isCheckingStatus.value = true;
    const previousAdapterValue = paymentAdapter.value;

    try {
      const response = await api.getPaymentStatus(basketUuid.value);

      if (!response) {
        return {
          success: false,
          adapterSwitched: false,
          previousAdapter: previousAdapterValue,
          currentAdapter: previousAdapterValue,
        };
      }

      // Check for payment error from backend
      if (response.paymentError) {
        error.value = response.paymentError;
        return {
          success: false,
          adapterSwitched: false,
          previousAdapter: previousAdapterValue,
          currentAdapter: previousAdapterValue,
        };
      }

      // Update order ID and state if changed
      orderId.value = response.orderId;
      orderState.value = response.orderState || null;

      // Detect adapter switch
      const adapterSwitched = previousAdapterValue !== response.paymentAdapter;

      // Update state
      paymentAdapter.value = response.paymentAdapter;
      payment.value = response.payment || null;
      paymentId.value = response.payment?.paymentId || null;

      // Update customer data (may have changed via sync)
      if (response.customer) {
        customer.value = response.customer;
      }

      return {
        success: true,
        adapterSwitched,
        previousAdapter: previousAdapterValue,
        currentAdapter: response.paymentAdapter,
      };
    } catch (e) {
      console.error('Norce Checkout status check error:', e);
      return {
        success: false,
        adapterSwitched: false,
        previousAdapter: previousAdapterValue,
        currentAdapter: previousAdapterValue,
      };
    } finally {
      isCheckingStatus.value = false;
    }
  };

  // ============================================
  // Payment Adapter Management
  // ============================================

  /**
   * Load available payment adapters
   * Call this on checkout load to determine which payment options to show
   */
  const loadAvailableAdapters = async(): Promise<boolean> => {
    try {
      const response = await api.getAvailableAdapters();

      if (response) {
        availableAdapters.value = response.available;
        defaultAdapter.value = response.default;
        hasInvoice.value = response.hasInvoice;
        return true;
      }

      return false;
    } catch (e) {
      console.error('Norce Checkout load adapters error:', e);
      return false;
    }
  };

  /**
   * Switch to a different payment adapter
   * Use this when user wants to switch between payment methods (e.g., Adyen ↔ Klarna)
   */
  const switchToAdapter = async(adapter: PaymentAdapter): Promise<boolean> => {
    if (!basketUuid.value) {
      error.value = 'Checkout not initialized';
      return false;
    }

    if (isSwitchingAdapter.value) {
      return false;
    }

    // Don't switch if already on this adapter
    if (paymentAdapter.value === adapter) {
      return true;
    }

    isSwitchingAdapter.value = true;
    error.value = null;

    try {
      const response = await api.switchAdapter(basketUuid.value, adapter);

      if (response?.switched) {
        orderId.value = response.orderId;
        paymentId.value = response.paymentId;
        paymentAdapter.value = response.paymentAdapter;
        payment.value = response.payment || null;

        // Update customer data if provided
        if (response.customer) {
          customer.value = response.customer;
        }

        return true;
      }

      error.value = api.lastError?.value?.message || 'Failed to switch payment adapter';
      return false;
    } catch (e) {
      error.value = 'An unexpected error occurred while switching payment method';
      console.error('Norce Checkout switch adapter error:', e);
      return false;
    } finally {
      isSwitchingAdapter.value = false;
    }
  };

  // ============================================
  // Non-PSP Payment Methods
  // ============================================

  /**
   * Select a Non-PSP payment method
   * Used for free orders where no actual payment processing is needed
   */
  const selectNonPspMethod = async(paymentMethod: string): Promise<boolean> => {
    if (!orderId.value) {
      error.value = 'Checkout not initialized';
      return false;
    }

    if (isProcessing.value) {
      return false;
    }

    isProcessing.value = true;
    error.value = null;

    try {
      const response = await api.selectNonPspPaymentMethod(orderId.value, paymentMethod);

      if (response) {
        payment.value = response.payment;
        return true;
      }

      error.value = api.lastError?.value?.message || 'Failed to select payment method';
      return false;
    } catch (e) {
      error.value = 'An unexpected error occurred';
      console.error('Norce Checkout select method error:', e);
      return false;
    } finally {
      isProcessing.value = false;
    }
  };

  /**
   * Complete a Non-PSP payment
   * Finalizes the order for free orders
   */
  const completeNonPsp = async(): Promise<{ success: boolean; order?: NorceOrderResult }> => {
    if (!orderId.value || !paymentId.value) {
      error.value = 'Checkout not initialized';
      return { success: false };
    }

    if (isProcessing.value) {
      return { success: false };
    }

    isProcessing.value = true;
    error.value = null;

    try {
      const response = await api.completeNonPspPayment(orderId.value, paymentId.value);

      if (response?.success) {
        return { success: true, order: response.order };
      }

      error.value = api.lastError?.value?.message || 'Failed to complete order';
      return { success: false };
    } catch (e) {
      error.value = 'An unexpected error occurred';
      console.error('Norce Checkout complete error:', e);
      return { success: false };
    } finally {
      isProcessing.value = false;
    }
  };

  // ============================================
  // Customer Address Updates (B2C only)
  // ============================================

  /**
   * Update billing address via Norce Checkout
   * Used for B2C checkout when Norce Checkout address updates are enabled
   */
  const updateBillingAddress = async(
    request: Omit<UpdateAddressRequest, 'orderId'>
  ): Promise<boolean> => {
    if (!orderId.value) {
      error.value = 'Checkout not initialized';
      return false;
    }

    // Norce rejects /customer updates once the order leaves checkout/processing.
    // Skip the call entirely for non-editable states to avoid a guaranteed 400.
    if (!isOrderEditable.value) {
      console.warn(`Norce Checkout: skipping billing address update, order state '${orderState.value}' is not editable`);
      return false;
    }

    if (isUpdatingAddress.value) {
      return false;
    }

    isUpdatingAddress.value = true;
    error.value = null;

    try {
      const response = await api.updateBillingAddress({
        ...request,
        orderId: orderId.value,
      });

      if (response) {
        // Update customer data with response
        if (response.customer) {
          customer.value = response.customer;
        }
        return true;
      }

      error.value = api.lastError?.value?.message || 'Failed to update billing address';
      return false;
    } catch (e) {
      error.value = 'An unexpected error occurred while updating billing address';
      console.error('Norce Checkout billing address update error:', e);
      return false;
    } finally {
      isUpdatingAddress.value = false;
    }
  };

  /**
   * Update shipping address via Norce Checkout
   * Used for B2C checkout when Norce Checkout address updates are enabled
   */
  const updateShippingAddress = async(
    request: Omit<UpdateAddressRequest, 'orderId'>
  ): Promise<boolean> => {
    if (!orderId.value) {
      error.value = 'Checkout not initialized';
      return false;
    }

    // Norce rejects /customer updates once the order leaves checkout/processing.
    // Skip the call entirely for non-editable states to avoid a guaranteed 400.
    if (!isOrderEditable.value) {
      console.warn(`Norce Checkout: skipping shipping address update, order state '${orderState.value}' is not editable`);
      return false;
    }

    if (isUpdatingAddress.value) {
      return false;
    }

    isUpdatingAddress.value = true;
    error.value = null;

    try {
      const response = await api.updateShippingAddress({
        ...request,
        orderId: orderId.value,
      });

      if (response) {
        // Update customer data with response
        if (response.customer) {
          customer.value = response.customer;
        }
        return true;
      }

      error.value = api.lastError?.value?.message || 'Failed to update shipping address';
      return false;
    } catch (e) {
      error.value = 'An unexpected error occurred while updating shipping address';
      console.error('Norce Checkout shipping address update error:', e);
      return false;
    } finally {
      isUpdatingAddress.value = false;
    }
  };

  /**
   * Reset checkout state
   * Call when leaving checkout or on error
   */
  const reset = () => {
    basketUuid.value = null;
    orderId.value = null;
    orderState.value = null;
    paymentId.value = null;
    paymentAdapter.value = null;
    payment.value = null;
    customer.value = null;
    availableAdapters.value = [];
    defaultAdapter.value = null;
    hasInvoice.value = false;
    isInitializing.value = false;
    isProcessing.value = false;
    isCheckingStatus.value = false;
    isUpdatingAddress.value = false;
    isSwitchingAdapter.value = false;
    error.value = null;
    resultCode.value = null;
  };

  /**
   * Get current state as a readonly object
   */
  const getState = (): NorceCheckoutState => ({
    orderId: orderId.value,
    orderState: orderState.value,
    paymentId: paymentId.value,
    paymentAdapter: paymentAdapter.value,
    payment: payment.value,
    customer: customer.value,
    availableAdapters: availableAdapters.value,
    defaultAdapter: defaultAdapter.value,
    hasInvoice: hasInvoice.value,
    isInitializing: isInitializing.value,
    isProcessing: isProcessing.value,
    isSwitchingAdapter: isSwitchingAdapter.value,
    error: error.value,
    resultCode: resultCode.value,
  });

  return {
    // State (readonly refs)
    basketUuid: readonly(basketUuid),
    orderId: readonly(orderId),
    orderState: readonly(orderState),
    paymentId: readonly(paymentId),
    paymentAdapter: readonly(paymentAdapter),
    payment: readonly(payment),
    customer: readonly(customer),
    availableAdapters: readonly(availableAdapters),
    defaultAdapter: readonly(defaultAdapter),
    hasInvoice: readonly(hasInvoice),
    isInitializing: readonly(isInitializing),
    isProcessing: readonly(isProcessing),
    isCheckingStatus: readonly(isCheckingStatus),
    isUpdatingAddress: readonly(isUpdatingAddress),
    isSwitchingAdapter: readonly(isSwitchingAdapter),
    error: readonly(error),
    resultCode: readonly(resultCode),

    // Computed helpers
    isAdyen,
    isKlarna,
    isWalley,
    isNonPsp,
    isOrderEditable,
    hasMultiplePspAdapters,

    // Adyen methods
    initialize,
    submitPayment,
    submitDetails,
    refresh,

    // Status checking (for adapter switches)
    checkPaymentStatus,

    // Adapter management
    loadAvailableAdapters,
    switchToAdapter,

    // Non-PSP methods
    selectNonPspMethod,
    completeNonPsp,

    // Customer address updates (B2C)
    updateBillingAddress,
    updateShippingAddress,

    // Common methods
    reset,
    getState,
  };
}
